import React, { useEffect, useId, useRef } from 'react';

import { Paper } from '@mui/material';
import clsx from 'clsx';
import {
  AlineButton,
  AlineNumberInput,
  defaultEditorPopoverPaperStyles,
  SmallCheckbox,
} from 'src/components';
import { useFocused } from 'src/layout-engine';
import { makeStyles } from 'tss-react/mui';

import { closeSplitTableCellsPopover } from './split-table-cells-popover-state';
import { useSplitTableCellsForm } from './use-split-table-cells-form';

const useStyles = makeStyles()(theme => {
  const buttonSize = { fontSize: 12, height: 24, padding: theme.spacing(0.5, 2) };

  return {
    buttons: {
      display: 'flex',
      gap: theme.spacing(1.5),
      justifyContent: 'flex-end',
      paddingTop: theme.spacing(1),
    },
    cancelButton: {
      '&&': {
        ...buttonSize,
        backgroundColor: theme.palette.common.white,
        border: `1px solid ${theme.appColors.rule}`,
        color: theme.appColors.iconsType,
      },
    },
    countInput: { width: 43 },
    countInputBase: {
      '& input': { fontSize: 13, height: '100%', padding: theme.spacing(0, 1.25) },
      height: '100%',
    },
    countInputBorder: {
      '&&': { borderRadius: 6 },
      height: 36,
    },
    disabled: { opacity: 0.5 },
    doneButton: {
      '&&': { ...buttonSize, fontWeight: 700, width: 67 },
    },
    field: {
      alignItems: 'center',
      display: 'flex',
      gap: theme.spacing(0.75),
    },
    fieldLabel: {
      ...theme.appTypography.fieldLabel,
      width: 120,
    },
    fields: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
    },
    paper: {
      ...defaultEditorPopoverPaperStyles,
      borderRadius: 12,
      display: 'flex',
      filter: 'drop-shadow(0px 10px 12.5px rgba(0, 0, 0, 0.15))',
      flexDirection: 'column',
      gap: theme.spacing(1),
      minWidth: 'unset',
      padding: theme.spacing(2),
      width: 223,
    },
    title: {
      color: theme.appColors.iconsType,
      fontSize: 14,
      fontWeight: 700,
    },
  };
});

export function SplitTableCellsPopoverContent(): JSX.Element {
  const { classes } = useStyles();
  const titleId = useId();
  const isEditorFocused = useFocused();
  const columnCountInputRef = useRef<HTMLInputElement>(null);
  const hasFocusedColumnCountRef = useRef(false);
  const {
    canMergeBeforeSplit,
    canSplitRows,
    cancel,
    columnCount,
    handleColumnCountChange,
    handleMergeBeforeSplitChange,
    handleRowCountChange,
    isMergingBeforeSplit,
    rowCount,
    splitCells,
  } = useSplitTableCellsForm();

  /**
   * Focus the column count once, in an effect rather than with autoFocus. Opening the popover from
   * the toolbar's overflow menu closes that modal menu in the same render, and it returns focus to
   * its trigger in an effect cleanup, which React runs before this effect but after autoFocus.
   */
  useEffect(() => {
    if (hasFocusedColumnCountRef.current) return;

    hasFocusedColumnCountRef.current = true;
    columnCountInputRef.current?.focus();
  }, []);

  // Returning focus to the editor dismisses the popover.
  useEffect(() => {
    if (isEditorFocused) closeSplitTableCellsPopover();
  }, [isEditorFocused]);

  function handleSubmit(event: React.FormEvent): void {
    event.preventDefault();
    splitCells();
  }

  function handleKeyDown(event: React.KeyboardEvent): void {
    if (event.key === 'Escape') cancel();
  }

  return (
    <Paper
      aria-labelledby={titleId}
      className={classes.paper}
      component="form"
      role="dialog"
      onKeyDown={handleKeyDown}
      onSubmit={handleSubmit}
    >
      <div className={classes.title} id={titleId}>
        Split Cells
      </div>

      <div className={classes.fields}>
        <SplitCountField
          inputRef={columnCountInputRef}
          label="Number of columns"
          value={columnCount}
          onChange={handleColumnCountChange}
        />
        <SplitCountField
          disabled={!canSplitRows}
          label="Number of rows"
          value={rowCount}
          onChange={handleRowCountChange}
        />
      </div>

      <SmallCheckbox
        checked={isMergingBeforeSplit}
        disabled={!canMergeBeforeSplit}
        label="Merge cells before split"
        onChange={event => handleMergeBeforeSplitChange(event.target.checked)}
      />

      <div className={classes.buttons}>
        <AlineButton buttonClassName={classes.cancelButton} onClick={cancel}>
          Cancel
        </AlineButton>
        <AlineButton
          buttonClassName={classes.doneButton}
          color="primary"
          type="submit"
          variant="contained"
        >
          Done
        </AlineButton>
      </div>
    </Paper>
  );
}

interface SplitCountFieldProps {
  label: string;
  onChange: (value: number | undefined) => void;
  value: number;
  disabled?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
}

function SplitCountField({
  disabled,
  inputRef,
  label,
  onChange,
  value,
}: SplitCountFieldProps): JSX.Element {
  const { classes } = useStyles();

  return (
    <label className={clsx(classes.field, disabled && classes.disabled)}>
      <span className={classes.fieldLabel}>{label}</span>
      <AlineNumberInput
        hideHelperText
        allowNegative={false}
        className={classes.countInput}
        decimalScale={0}
        disabled={disabled}
        fullWidth={false}
        inputBaseClassName={classes.countInputBase}
        inputClassName={classes.countInputBorder}
        inputRef={inputRef}
        value={value}
        variant="default"
        onFocus={event => event.target.select()}
        onValueChange={({ floatValue }) => onChange(floatValue)}
      />
    </label>
  );
}
